// Shapes the "by course x location, multi-year trend" data (from
// authenticatedDataverseService.getRegistrationsByCourseLocation) into Excel
// array-of-arrays sheets - for BOTH metrics:
//   - registrations (attendees)  -> row.counts / row.total
//   - sessions (classes held)    -> row.sessionCounts / row.sessionTotal
//
// The LAST year in `report.years` is the current year and is YTD (partial); all
// earlier years are FULL calendar years. Year-over-year change columns are ONLY
// produced between consecutive FULL years, because comparing a partial YTD year
// against a full prior year is not apples-to-apples. Rows are sorted by course.
//
// Pure (no XLSX / DOM / network) so it can be unit tested.

import { HUB_CODES, hubName } from './hubLocation';

function columnName(index) {
  let name = '';
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
  }
  return name;
}

function formulaCell(formula, value) {
  return { t: 'n', f: formula, v: value };
}

// Metric accessors: how to read a year's value for each metric.
const METRICS = {
  registrations: { at: (r, y) => (r.counts && r.counts[y]) || 0 },
  sessions: { at: (r, y) => (r.sessionCounts && r.sessionCounts[y]) || 0 }
};

// Column layout for a given `years` array. The last year is YTD; the rest are
// full. Comparison columns exist only between consecutive full years.
function layout(years) {
  const fullYears = years.slice(0, -1);        // all but the current YTD year
  const currentYear = years[years.length - 1]; // YTD year
  const pairs = [];                            // [{ prev, curr }] full-year pairs
  for (let i = 1; i < fullYears.length; i++) {
    pairs.push({ prev: fullYears[i - 1], curr: fullYears[i] });
  }
  return { fullYears, currentYear, pairs };
}

/**
 * Build one filterable row per Course x Location with registrations and
 * sessions side by side for every year. This is the executive review sheet;
 * the metric-specific tabs remain available for focused analysis.
 *
 * @param {object} report result of getRegistrationsByCourseLocation
 * @returns {{ aoa:any[][], colWidths:object[], autofilterRef:object, deltaCols:number[], currencyCols:number[] }}
 */
export function consolidatedTrendToWorkbookData(report) {
  const years = report.years || [];
  const rows = Array.isArray(report.rows) ? report.rows : [];
  const { currentYear, pairs } = layout(years);
  const dimensions = ['Course', 'Course Cost', 'Campus', 'Hub', 'Location'];
  const yearColumns = years.flatMap((year, index) => [
    { year, metric: 'registrations', col: dimensions.length + index * 2 },
    { year, metric: 'sessions', col: dimensions.length + index * 2 + 1 }
  ]);
  const deltaStart = dimensions.length + yearColumns.length;
  const deltaColumns = pairs.flatMap((pair, index) => [
    { ...pair, metric: 'registrations', col: deltaStart + index * 2 },
    { ...pair, metric: 'sessions', col: deltaStart + index * 2 + 1 }
  ]);
  const width = dimensions.length + yearColumns.length + deltaColumns.length;
  const header = new Array(width).fill('');
  dimensions.forEach((label, col) => { header[col] = label; });
  yearColumns.forEach(({ year, metric, col }) => {
    const period = year === currentYear ? `${year} YTD` : String(year);
    header[col] = `${period} ${metric === 'registrations' ? 'Registrations' : 'Sessions'}`;
  });
  deltaColumns.forEach(({ prev, curr, metric, col }) => {
    header[col] = `${metric === 'registrations' ? 'Registration' : 'Session'} Δ ${curr} vs ${prev}`;
  });

  const sorted = [...rows].sort((a, b) =>
    (a.course || '').localeCompare(b.course || '', undefined, { sensitivity: 'base' }) ||
    (a.location || '').localeCompare(b.location || '', undefined, { sensitivity: 'base' })
  );
  const aoa = [header];
  const totals = Object.fromEntries(yearColumns.map(({ col }) => [col, 0]));
  sorted.forEach(source => {
    const row = new Array(width).fill('');
    row[0] = source.course || '';
    row[1] = Number.isFinite(source.courseCost) ? source.courseCost : '';
    row[2] = source.campus || '';
    row[3] = source.hub === 'Unclassified' ? 'Unclassified' : `${source.hub} ${hubName(source.hub)}`.trim();
    row[4] = source.location || '';
    yearColumns.forEach(({ year, metric, col }) => {
      const value = metric === 'registrations'
        ? Number(source.counts?.[year] || 0)
        : Number(source.sessionCounts?.[year] || 0);
      row[col] = value;
      totals[col] += value;
    });
    deltaColumns.forEach(({ prev, curr, metric, col }) => {
      const values = metric === 'registrations' ? source.counts : source.sessionCounts;
      row[col] = Number(values?.[curr] || 0) - Number(values?.[prev] || 0);
    });
    aoa.push(row);
  });

  const grand = new Array(width).fill('');
  grand[0] = 'GRAND TOTAL';
  const firstDataRow = 2;
  const lastDataRow = sorted.length + 1;
  yearColumns.forEach(({ col }) => {
    grand[col] = sorted.length
      ? formulaCell(`SUM(${columnName(col)}${firstDataRow}:${columnName(col)}${lastDataRow})`, totals[col])
      : 0;
  });
  deltaColumns.forEach(({ prev, curr, metric, col }) => {
    const previous = yearColumns.find(item => item.year === prev && item.metric === metric);
    const current = yearColumns.find(item => item.year === curr && item.metric === metric);
    const value = (totals[current.col] || 0) - (totals[previous.col] || 0);
    grand[col] = sorted.length
      ? formulaCell(`SUM(${columnName(col)}${firstDataRow}:${columnName(col)}${lastDataRow})`, value)
      : 0;
  });
  aoa.push(grand);

  return {
    aoa,
    colWidths: [
      { wch: 56 }, { wch: 14 }, { wch: 20 }, { wch: 16 }, { wch: 28 },
      ...yearColumns.map(() => ({ wch: 20 })),
      ...deltaColumns.map(() => ({ wch: 24 }))
    ],
    autofilterRef: { s: { r: 0, c: 0 }, e: { r: sorted.length, c: width - 1 } },
    deltaCols: deltaColumns.map(({ col }) => col),
    currencyCols: [1]
  };
}

/**
 * Build a flat, filterable "By Course x Location" sheet for one metric.
 *
 * Registration columns: Course, Course Cost, Campus, Hub, Location. Session
 * columns omit Course Cost. Both then include one column per year (current year
 * labelled "YTD"), a numeric delta for each consecutive full-year comparison,
 * and a grand-total row. Rows are sorted by course A-Z.
 *
 * @param {object} report result of getRegistrationsByCourseLocation
 * @param {'registrations'|'sessions'} [metric] which metric to render
 * @returns {{ aoa:any[][], colWidths:object[], autofilterRef:object, deltaCols:number[], currencyCols:number[] }}
 */
export function trendToWorkbookData(report, metric = 'registrations') {
  const at = (METRICS[metric] || METRICS.registrations).at;
  const years = report.years || [];
  const rows = Array.isArray(report.rows) ? report.rows : [];
  const { currentYear, pairs } = layout(years);

  const includeCourseCost = metric === 'registrations';
  const C = includeCourseCost
    ? { course: 0, cost: 1, campus: 2, hub: 3, location: 4 }
    : { course: 0, campus: 1, hub: 2, location: 3 };
  const yStart = includeCourseCost ? 5 : 4;
  const yCol = years.map((_, i) => yStart + i);
  const dStart = yStart + years.length;
  const dNumCol = pairs.map((_, i) => dStart + i);
  const width = dStart + pairs.length;

  const blankRow = () => new Array(width).fill('');
  const cellVal = v => (v ? v : '');

  const header = blankRow();
  header[C.course] = 'Course';
  if (includeCourseCost) header[C.cost] = 'Course Cost';
  header[C.campus] = 'Campus';
  header[C.hub] = 'Hub';
  header[C.location] = 'Location';
  years.forEach((y, i) => { header[yCol[i]] = y === currentYear ? `${y} YTD` : `${y}`; });
  pairs.forEach((p, i) => {
    header[dNumCol[i]] = `\u0394 ${p.curr} vs ${p.prev}`;
  });

  // Modern JavaScript's stable sort preserves source order among locations for
  // the same course, matching an Excel A-Z sort on the Course column.
  const sorted = [...rows].sort((a, b) =>
    (a.course || '').localeCompare(b.course || '', undefined, { sensitivity: 'base' })
  );

  const aoa = [header];
  const totals = {};
  years.forEach(y => { totals[y] = 0; });

  const writeMetricCols = (row, get) => {
    years.forEach((y, i) => { row[yCol[i]] = cellVal(get(y)); });
    pairs.forEach((p, i) => {
      const prev = get(p.prev), curr = get(p.curr);
      row[dNumCol[i]] = curr - prev;
    });
  };

  sorted.forEach(r => {
    years.forEach(y => { totals[y] += at(r, y); });
    const row = blankRow();
    row[C.course] = r.course;
    if (includeCourseCost) row[C.cost] = Number.isFinite(r.courseCost) ? r.courseCost : '';
    row[C.campus] = r.campus;
    row[C.hub] = r.hub === 'Unclassified' ? 'Unclassified' : `${r.hub} ${hubName(r.hub)}`.trim();
    row[C.location] = r.location;
    writeMetricCols(row, y => at(r, y));
    aoa.push(row);
  });

  const grand = blankRow();
  grand[C.course] = 'GRAND TOTAL';
  const firstDataRow = 2;
  const lastDataRow = sorted.length + 1;
  years.forEach((y, i) => {
    grand[yCol[i]] = sorted.length
      ? formulaCell(`SUM(${columnName(yCol[i])}${firstDataRow}:${columnName(yCol[i])}${lastDataRow})`, totals[y])
      : 0;
  });
  pairs.forEach((p, i) => {
    grand[dNumCol[i]] = sorted.length
      ? formulaCell(`SUM(${columnName(dNumCol[i])}${firstDataRow}:${columnName(dNumCol[i])}${lastDataRow})`, totals[p.curr] - totals[p.prev])
      : 0;
  });
  aoa.push(grand);

  const colWidths = new Array(width).fill(null).map((_, i) => {
    if (i === C.course) return { wch: 56 };
    if (includeCourseCost && i === C.cost) return { wch: 14 };
    if (i === C.campus) return { wch: 16 };
    if (i === C.hub) return { wch: 16 };
    if (i === C.location) return { wch: 22 };
    return { wch: 13 };
  });

  const autofilterRef = { s: { r: 0, c: 0 }, e: { r: sorted.length, c: width - 1 } };

  return {
    aoa,
    colWidths,
    autofilterRef,
    deltaCols: dNumCol,
    currencyCols: includeCourseCost ? [C.cost] : []
  };
}

/**
 * Build the "By Hub" rollup sheet for one metric: one row per hub
 * (WA/HI/CA/VA/Unclassified) with year columns + full-year YoY change, and a
 * grand total.
 *
 * @param {object} report result of getRegistrationsByCourseLocation
 * @param {'registrations'|'sessions'} [metric]
 * @returns {{ aoa:any[][], colWidths:object[], deltaCols:number[] }}
 */
export function trendHubRollup(report, metric = 'registrations') {
  const at = (METRICS[metric] || METRICS.registrations).at;
  const years = report.years || [];
  const rows = Array.isArray(report.rows) ? report.rows : [];
  const { currentYear, pairs } = layout(years);

  const hubCol = 0;
  const yStart = 1;
  const yCol = years.map((_, i) => yStart + i);
  const dStart = yStart + years.length;
  const dNumCol = pairs.map((_, i) => dStart + i);
  const width = dStart + pairs.length;
  const blankRow = () => new Array(width).fill('');

  const order = [...HUB_CODES, 'Unclassified'];
  const byHub = new Map(order.map(h => {
    const agg = {}; years.forEach(y => { agg[y] = 0; }); return [h, agg];
  }));
  rows.forEach(r => {
    const h = order.includes(r.hub) ? r.hub : 'Unclassified';
    const agg = byHub.get(h);
    years.forEach(y => { agg[y] += at(r, y); });
  });

  const header = blankRow();
  header[hubCol] = 'Hub';
  years.forEach((y, i) => { header[yCol[i]] = y === currentYear ? `${y} YTD` : `${y}`; });
  pairs.forEach((p, i) => {
    header[dNumCol[i]] = `\u0394 ${p.curr} vs ${p.prev}`;
  });

  const aoa = [header];
  const grandTotals = {};
  years.forEach(y => { grandTotals[y] = 0; });

  const writeHubRow = (label, agg) => {
    const row = blankRow();
    row[hubCol] = label;
    years.forEach((y, i) => { row[yCol[i]] = agg[y]; });
    const excelRow = aoa.length + 1;
    pairs.forEach((p, i) => {
      const prevCol = yCol[years.indexOf(p.prev)];
      const currCol = yCol[years.indexOf(p.curr)];
      row[dNumCol[i]] = formulaCell(
        `${columnName(currCol)}${excelRow}-${columnName(prevCol)}${excelRow}`,
        agg[p.curr] - agg[p.prev]
      );
    });
    aoa.push(row);
  };

  const visibleOrder = order.filter(h => h !== 'Unclassified' || years.some(y => byHub.get(h)[y] !== 0));
  visibleOrder.forEach(h => {
    const agg = byHub.get(h);
    years.forEach(y => { grandTotals[y] += agg[y]; });
    writeHubRow(h === 'Unclassified' ? 'Unclassified' : `${h} ${hubName(h)}`.trim(), agg);
  });

  const grand = blankRow();
  grand[hubCol] = 'GRAND TOTAL';
  const firstHubRow = 2;
  const lastHubRow = visibleOrder.length + 1;
  years.forEach((y, i) => {
    grand[yCol[i]] = formulaCell(
      `SUM(${columnName(yCol[i])}${firstHubRow}:${columnName(yCol[i])}${lastHubRow})`,
      grandTotals[y]
    );
  });
  pairs.forEach((p, i) => {
    grand[dNumCol[i]] = formulaCell(
      `SUM(${columnName(dNumCol[i])}${firstHubRow}:${columnName(dNumCol[i])}${lastHubRow})`,
      grandTotals[p.curr] - grandTotals[p.prev]
    );
  });
  aoa.push(grand);

  const colWidths = new Array(width).fill(null).map((_, i) => {
    if (i === hubCol) return { wch: 18 };
    return { wch: 13 };
  });

  return { aoa, colWidths, deltaCols: dNumCol };
}

/**
 * Build the session-status comparison available from the reliable tracking
 * start (January 2025), with one row per course/location. Scheduled is every
 * session placed on the calendar; Cancelled and Held are outcome subsets.
 *
 * @param {object} report result of getRegistrationsByCourseLocation
 * @returns {{ aoa:any[][], colWidths:object[], autofilterRef:object, deltaCols:number[] }}
 */
export function sessionStatusComparison(report) {
  const years = report.sessionStatusYears || [2025, 2026];
  const dimensions = ['Course', 'Campus', 'Hub', 'Location'];
  const statuses = [
    ['Scheduled', 'scheduled'],
    ['Cancelled', 'cancelled'],
    ['Held', 'held']
  ];
  const rows = Array.isArray(report.sessionStatusByCourse) ? report.sessionStatusByCourse : [];
  const valueCols = years.flatMap((year, yearIndex) =>
    statuses.map(([label, key], statusIndex) => ({ year, label, key, col: dimensions.length + yearIndex * statuses.length + statusIndex }))
  );
  const scheduledCols = valueCols.filter(({ key }) => key === 'scheduled');
  const width = dimensions.length + valueCols.length + 1;
  const totalCol = width - 1;
  const header = new Array(width).fill('');
  dimensions.forEach((label, col) => { header[col] = label; });
  valueCols.forEach(({ year, label, col }) => {
    const period = year === 2025 ? '2025 (Full Year)' : year === 2026 ? '2026 (Full Year)' : String(year);
    header[col] = `${period} ${label}`;
  });
  header[totalCol] = 'Total Scheduled';

  const aoa = [header];
  const columnTotals = Object.fromEntries(valueCols.map(({ col }) => [col, 0]));
  [...rows]
    .sort((a, b) =>
      (a.course || '').localeCompare(b.course || '', undefined, { sensitivity: 'base' }) ||
      (a.location || '').localeCompare(b.location || '', undefined, { sensitivity: 'base' })
    )
    .forEach(statusRow => {
      const excelRow = aoa.length + 1;
      const row = new Array(width).fill('');
      row[0] = statusRow.course;
      row[1] = statusRow.campus || '';
      row[2] = statusRow.hub || 'Unclassified';
      row[3] = statusRow.location || '';
      valueCols.forEach(({ year, key, col }) => {
        const value = Number(statusRow[key]?.[year]) || 0;
        row[col] = value;
        columnTotals[col] += value;
      });
      row[totalCol] = formulaCell(
        `SUM(${scheduledCols.map(({ col }) => `${columnName(col)}${excelRow}`).join(',')})`,
        scheduledCols.reduce((sum, { year, key }) => sum + (Number(statusRow[key]?.[year]) || 0), 0)
      );
      aoa.push(row);
    });

  const grandRowNumber = aoa.length + 1;
  const grand = new Array(width).fill('');
  grand[0] = 'GRAND TOTAL';
  valueCols.forEach(({ col }) => {
    grand[col] = formulaCell(`SUM(${columnName(col)}2:${columnName(col)}${grandRowNumber - 1})`, columnTotals[col]);
  });
  grand[totalCol] = formulaCell(
    `SUM(${columnName(totalCol)}2:${columnName(totalCol)}${grandRowNumber - 1})`,
    scheduledCols.reduce((sum, { col }) => sum + columnTotals[col], 0)
  );
  aoa.push(grand);

  return {
    aoa,
    colWidths: [{ wch: 56 }, { wch: 20 }, { wch: 14 }, { wch: 28 }, ...valueCols.map(() => ({ wch: 24 })), { wch: 16 }],
    autofilterRef: { s: { r: 0, c: 0 }, e: { r: rows.length, c: width - 1 } },
    deltaCols: []
  };
}

// Backwards-compatible named exports (registrations metric).
export const registrationTrendToWorkbookData = report => trendToWorkbookData(report, 'registrations');
export const registrationTrendHubRollup = report => trendHubRollup(report, 'registrations');
